<!--
  An artist who has published a website gets that rendered here; everyone else gets the profile
  layout below, unchanged. One public URL that improves once they publish, rather than a second
  address to explain.
-->
@if (!siteResolved) {
  <!-- Neither layout until we know which one is right. Rendering the profile first and swapping
       to the website a beat later flashed the wrong page at every visitor. -->
  <div class="site-resolving"></div>
} @else if (publishedSite) {
  <app-site-view [content]="publishedSite" [works]="siteWorks" [currency]="siteCurrency"
                 [identifier]="siteIdentifier"></app-site-view>
} @else {
<div class="page-inner-body portlet light">
  <div class="portlet-body ">
    <div class="table-toolbar">
      <!-- Breadcrumb for authenticated users with items -->
      @if (artist || gallery) {
        <!-- Your existing HTML -->
        <p-breadcrumb [model]="items" [home]="home"></p-breadcrumb>
      } @else {
        <div class="skeleton-profile-loader">
          <!-- Profile Header Skeleton -->
          <div class="d-flex gap-4 mb-4">
            <div class="skeleton" style="width: 120px; height: 120px; border-radius: 50%;"></div>
            <div class="flex-grow-1">
              <div class="skeleton" style="width: 200px; height: 24px;"></div>
              <div class="skeleton" style="width: 150px; height: 16px; margin-top: 10px;"></div>
              <div class="skeleton" style="width: 300px; height: 14px; margin-top: 12px;"></div>
              <div class="d-flex gap-2 mt-3">
                <div class="skeleton" style="width: 36px; height: 36px; border-radius: 50%;"></div>
                <div class="skeleton" style="width: 36px; height: 36px; border-radius: 50%;"></div>
                <div class="skeleton" style="width: 36px; height: 36px; border-radius: 50%;"></div>
              </div>
            </div>
          </div>
          <!-- Content Sections Skeleton -->
          <div class="skeleton" style="width: 160px; height: 20px; margin-bottom: 1rem;"></div>
          <div class="skeleton" style="width: 100%; height: 80px; border-radius: 10px; margin-bottom: 1.5rem;"></div>
          <!-- Artworks Grid Skeleton -->
          <div class="skeleton" style="width: 160px; height: 20px; margin-bottom: 1rem;"></div>
          <div class="d-flex gap-3">
            @for (i of [1,2,3]; track i) {
              <div class="skeleton" style="flex: 1; height: 200px; border-radius: 10px;"></div>
            }
          </div>
        </div>
      }

      <div class="row ms-2">
        <!-- Non-authenticated user view -->
        @if (!isAuthenticated) {
          <div class="d-flex flex-column align-items-center justify-content-center text-center py-5 mt-3"
            >
            <a href="javascript:void(0);" routerLink="/">
              <img src="assets/images/thumb_AFP-logo-v02-noTagline-800.png" height="100" class="mb-3" />
            </a>
            <div class="fs-4 mb-3">
              {{ 'viewArtistProfile.alreadyAMember' | transloco }}
              <a href="javascript:void(0);" routerLink="/login" class="fs-4 text-decoration-underline">
                {{ 'viewArtistProfile.login' | transloco }}
              </a>
            </div>
            <p class="text-muted">{{ 'viewArtistProfile.joinNow' | transloco }}</p>
          </div>
        }

        <!-- Artist Information Section -->
        <div class="row">
          <!-- Main Content Column -->
          <div [ngClass]="isAuthenticated ? 'col-12' : 'col-12'">
            @if (artistId) {
              <div class="mt-2">
                <!-- Artist Header with Navigation -->
                <div class="dsHeader">
                  <div class="dsTopBar">
                    <div class="dsName">{{ artist.Name || gallery.BusinessName }}</div>
                    <div class="dsMenu">
                      <ul class="cursor-pointer">
                        <li><a (click)="navigateToSection('home')">{{ 'viewArtistProfile.home' | transloco }}</a></li>
                        <li><a (click)="navigateToSection('works')">{{ 'viewArtistProfile.works' | transloco }}</a></li>
                        <li><a (click)="navigateToSection('resume')">{{ 'viewArtistProfile.resume' | transloco }}</a></li>
                        <li><a (click)="navigateToSection('contact')">{{ 'viewArtistProfile.contact' | transloco }}</a></li>
                      </ul>
                    </div>
                    <button class="mobile-menu-toggle" id="mobileMenuToggle"
                    (click)="toggleMenu()">☰</button>
                  </div>
                </div>
                <!-- Main Content Container -->
                <div class="dsContainer " [ngClass]="!isAuthenticated ? 'px-5' : 'px-2'">
                  <!-- Home Section -->
                  <section>
                    @if (artistId) {
                      <div id="home" class="dsHome ">
                        <!-- Artist Statement -->
                        <div class="dsArtistStatement ">
                          <div
                            class="d-flex flex-column flex-md-row align-items-center align-items-md-start justify-content-between mb-1 ">
                            <div class=" mb-md-0">
                              <h3>{{ 'viewArtistProfile.artistStatement' | transloco }}</h3>
                            </div>
                            <div class="d-flex justify-content-end">
                              @if (authConfig.IsArtist && artistId) {
                                <div class="p-toolbar-group-end"
                                  >
                                  <button pButton type="button" class="p-button-outlined ms-2"
                                    [routerLink]="['/edit-profile']">
                                    <i class="fa fa-user me-1"></i> {{ 'viewArtistProfile.exitPreview' | transloco }}
                                  </button>
                                </div>
                              }
                              <!-- Buttons -->
                              @if (authConfig.IsArtist || authConfig.IsGallery && artistId) {
                                <div class="p-toolbar-group-end"
                                  >
                                  <button pButton type="button" class="p-button-outlined ms-2"
                                    (click)="openShareDialog()">
                                    <i class="fa fa-copy me-1"></i> {{ 'viewArtistProfile.share' | transloco }}
                                  </button>
                                </div>
                              }
                              <!-- Share Dialog -->
                              @if (showShareDialog) {
                                <product-share [isProductShareDailogVisible]="showShareDialog"
                                  [artistName]="artist.UserName"
                                  (onCloseDailog)="onShareDialogClose($event)">
                                </product-share>
                              }
                            </div>
                          </div>
                          <div
                            [innerHTML]="artist.AboutMe ? (artist.AboutMe | safeHtml) : 'No Statement'">
                          </div>
                        </div>
                        <!-- Main Artist Image -->
                        @if (artist.PrimaryProductImageUrl) {
                          <div class="justify-content-center text-center mt-2"
                            >
                            <div class="form-group mt-2 d-flex justify-content-center mb-3 bg-white p-3 rounded-3 shadow-sm"
                              style="width: 70%; margin: 0 auto;">
                              <div class="position-relative d-inline-block mt-2 text-center">
                                <img [src]="artist.PrimaryProductImageUrl"
                                  class="PrimaryProductImageUrl img-fluid"
                                  style="display: block; margin: 0 auto; object-fit: cover; border-radius: 15px;" />
                                </div>
                              </div>
                            </div>
                          }
                        </div>
                      }
                    </section>
                    <!-- Works Section -->
                    <section>
                      <div id="works">
                        <h3>{{ 'viewArtistProfile.works' | transloco }}</h3>
                        <div class="mb-1">
                          <!-- Favorite Portfolios Section -->
                          @if (favoritePortfolioProducts.length > 0) {
                            <div class="row mt-1 mb-2">
                              <div class="col-12 ">
                                @for (portfolio of favoritePortfolioProducts; track portfolio) {
                                  <div
                                    class="mb-4">
                                    <!-- Portfolio Title + Description -->
                                    <div class="mb-4 border-bottom pb-2">
                                      @if (portfolio.FavouritePortfolioLinkViewModel?.Title) {
                                        <h5
                                          class="fw-bold mb-3 text-dark">
                                          {{ portfolio.FavouritePortfolioLinkViewModel?.Title }}
                                        </h5>
                                      }
                                      @if (portfolio.FavouritePortfolioLinkViewModel?.Description) {
                                        <p
                                          class="text-muted small mb-0">
                                          <i class="pi pi-info-circle me-1"></i>
                                          {{
                                          portfolio.FavouritePortfolioLinkViewModel?.Description
                                          }}
                                        </p>
                                      }
                                    </div>
                                    <!-- Empty State -->
                                    @if (!portfolio.PortfolioProductsViewModel || portfolio.PortfolioProductsViewModel.length === 0) {
                                      <div
                                        class="text-center text-muted py-4">
                                        <i class="pi pi-box me-1"></i>{{ 'viewArtistProfile.noPortfolioProducts' | transloco }}
                                      </div>
                                    }
                                    <!-- Product Grid -->
                                    <div class="row">
                                      @for (rowData of portfolio.PortfolioProductsViewModel; track rowData) {
                                        <div class="col-3 mb-3"
                                          >
                                          <div class="border-0 shadow-sm h-100 rounded-3 text-center cursor-pointer"
                                            (click)="showProductDeatils(rowData.ArtistProduct.Id)">
                                            <!-- Product Image -->
                                            <div class="bg-white py-2 rounded-top card-img-top">
                                              <img [src]="rowData.ArtistProduct.ResizedImageUrl || 'assets/images/No-Image.png'"
                                                alt="{{ rowData.ArtistProduct.Name }}"
                                                class="img-fluid rounded"
                                                style="max-height: 200px; width: auto;" />
                                              </div>
                                              <!-- Product Title -->
                                              <div class="card-body bg-light p-2">
                                                <h6
                                                  class="fw-semibold small mb-0 text-dark text-truncate">
                                                  <a href="javascript:void(0);"
                                                    class="text-decoration-none text-dark">
                                                    {{ rowData.ArtistProduct.Name }}
                                                  </a>
                                                </h6>
                                              </div>
                                            </div>
                                          </div>
                                        }
                                      </div>
                                    </div>
                                  }
                                </div>
                              </div>
                            }
                          </div>
                        </div>
                      </section>
                      <!-- Resume Section -->
                      <section>
                        <div id="resume">
                          <h2>{{ 'viewArtistProfile.resume' | transloco }}</h2>
                          @if (artistId) {
                            <div style="border-bottom:solid #595959 1.0pt;">
                              @if (artist.Resume) {
                                <div [innerHTML]="artist.Resume | safeHtml"
                                class="mb-3"></div>
                              }
                            </div>
                          }
                        </div>
                      </section>
                      <!-- Contact Section -->
                      <section>
                        <div id="contact">
                          <h2>{{ 'viewArtistProfile.contact' | transloco }}</h2>
                          @if (artistId) {
                            <div class="d-flex align-items-start mb-2"
                              style="border:none;border-bottom:solid #595959 1.0pt;">
                              <div
                                class="d-flex flex-column flex-md-row align-items-center align-items-md-start mb-4 w-100">
                                <!-- Artist Profile Image -->
                                <img [src]="artist.ImgUrl || 'assets/images/No-Image.png'"
                                  [alt]="'viewArtistProfile.profileImage' | transloco"
                                  class="rounded-circle border mb-3 mb-md-0 me-md-3"
                                  style="width: 120px; height: 120px; object-fit: cover;">
                                  <div class="text-center text-md-start">
                                    <!-- Artist Contact Information -->
                                    <div class="display-6 fw-bold">{{ artist.Name }}</div>
                                    <div class="text-secondary">
                                      @if (artist.Email) {
                                        <a [href]="'mailto:' + artist.Email">
                                          {{ artist.Email }}
                                        </a>
                                      }
                                    </div>
                                    @if (artist.MobileNumber) {
                                      <div class="text-secondary">
                                        {{ artist.MobileNumber }}
                                      </div>
                                    }
                                    @if (artistAddress) {
                                      <div class="mt-2">
                                        <i class="fa fa-map-marker me-2"></i>{{ artistAddress }}
                                      </div>
                                    }
                                    @if (artist.Website) {
                                      <div class="mt-2">
                                        <i class="fa fa-external-link-square me-2"></i>
                                        <a [href]="artist.Website" target="_blank">{{ artist.Website
                                        }}</a>
                                      </div>
                                    }
                                  </div>
                                </div>
                              </div>
                            }
                          </div>
                        </section>
                      </div>
                    </div>
                  }
                </div>
              </div>

              <!-- Mobile Menu Overlay -->
              <div class="mobile-menu-overlay" id="mobileMenuOverlay" [style.opacity]="menuOpen ? '1' : '0'"
              [style.display]="menuOpen ? 'block' : 'none'" (click)="toggleMenu()"></div>

              <!-- Mobile Menu -->
              <div class="mobile-menu" id="mobileMenu" [style.width]="isMobileScreen() ? '100%' : '280px'"
                [style.right]="menuOpen ? '0' : (isMobileScreen() ? '-100%' : '-280px')">
                <div class="mobile-menu-header">
                  <span>{{ artist.Name || gallery.BusinessName }}</span>
                  <button class="mobile-menu-close" id="mobileMenuClose" (click)="toggleMenu()">×</button>
                </div>
                <ul>
                  <li><a (click)="navigateToSection('home'); toggleMenu()" class="mobile-menu-link">{{ 'viewArtistProfile.home' | transloco }}</a></li>
                  <li><a (click)="navigateToSection('works'); toggleMenu()" class="mobile-menu-link">{{ 'viewArtistProfile.works' | transloco }}</a>
                </li>
                <li><a (click)="navigateToSection('resume'); toggleMenu()" class="mobile-menu-link">{{ 'viewArtistProfile.resume' | transloco }}</a>
              </li>
              <li><a (click)="navigateToSection('contact'); toggleMenu()" class="mobile-menu-link">{{ 'viewArtistProfile.contact' | transloco }}</a>
            </li>
          </ul>
        </div>
        <!-- Gallery Information Section -->

        <div class="mt-4">
          <div [ngClass]="isAuthenticated ? 'col-12' : 'col-11 mx-auto'">
            @if (galleryId) {
              <div class="rounded shadow-sm overflow-hidden">
                <!-- Gallery Header and Information -->
                <div class="bg-light p-2 p-md-5">
                  <div class="justify-content-end d-flex mb-3">
                    <!-- Buttons (pushed to bottom) -->
                    <div class="d-flex flex-column align-items-center gap-2 mt-auto">
                      @if (authConfig.IsGallery && galleryId) {
                        <div class="p-toolbar-group-end">
                          <button pButton type="button"
                            class="p-button-outlined p-button-rounded px-4 py-2 shadow-sm w-100"
                            [routerLink]="['/edit-profile']">
                            <i class="pi pi-user me-2"></i> {{ 'viewArtistProfile.editProfile' | transloco }}
                          </button>
                        </div>
                      }
                      @if (authConfig.IsArtist && isAuthenticated) {
                        <button pButton type="button"
                          class="p-button-outlined p-button-rounded px-4 py-2 shadow-sm w-100"
                          (click)="openTermsDialog()" [title]="'viewArtistProfile.viewGalleryTermsTitle' | transloco"
                          >
                          <i class="pi pi-file me-2"></i> {{ 'viewArtistProfile.termsAndConditions' | transloco }}
                        </button>
                      }
                    </div>
                  </div>
                  <div class="row g-4 gallery-profile-container"
                    style="display: flex; align-items: stretch;">
                    <!-- Single Column Layout for Better Responsiveness -->
                    <div class="col-12">
                      <div class="card border-0   p-4 d-flex flex-column">
                        <!-- Image Section - Top Center -->
                        <div class="profile-image-section text-center mb-4">
                          <div class="image-container">
                            <img [src]="gallery.GalleryImgUrl ? gallery.GalleryImgUrl : 'assets/images/No-Image.png'"
                              [alt]="'viewArtistProfile.galleryImageAlt' | transloco: { name: gallery.BusinessName }"
                              class="profile-img rounded-circle"
                              style="transition: transform 0.3s ease;" />
                            </div>
                          </div>
                          <!-- Business Name and Personal Name -->
                          <div class="business-info text-center mb-4">
                            <h2 class="mb-2 fw-bold fs-2 fs-md-1 text-primary">{{
                            gallery.BusinessName }}</h2>
                            @if (gallery.Name) {
                              <p class="mb-0 text-muted fs-5">{{ gallery.Name }}
                              </p>
                            }
                          </div>
                          <!-- Contact Information -->
                          @if (gallery.Email || gallery.PhoneNumber || gallery.Website) {
                            <div class="contact-section mb-4"
                              >
                              <h5 class="fw-bold  text-center">{{ 'viewArtistProfile.contactInformation' | transloco }}</h5>
                              <div class="contact-details d-flex flex-column gap-1">
                                @if (gallery.Email) {
                                  <div class="contact-item text-center">
                                    <div class="d-flex align-items-center justify-content-center">
                                      <i class="bi bi-envelope-fill me-2 text-primary"></i>
                                      <a href="mailto:{{ gallery.Email }}"
                                        class="text-dark text-decoration-none hover-link email-responsive">
                                        {{ gallery.Email }}
                                      </a>
                                    </div>
                                  </div>
                                }
                                @if (gallery.PhoneNumber) {
                                  <div class="contact-item text-center">
                                    <div class="d-flex align-items-center justify-content-center">
                                      <i class="bi bi-telephone-fill me-2 text-primary"></i>
                                      <a href="tel:{{ gallery.PhoneNumber }}"
                                        class="text-dark text-decoration-none hover-link">
                                        {{ gallery.PhoneNumber }}
                                      </a>
                                    </div>
                                  </div>
                                }
                                @if (gallery.Website) {
                                  <div class="contact-item text-center">
                                    <div class="d-flex align-items-center justify-content-center">
                                      <i class="bi bi-globe me-2 text-primary"></i>
                                      <a [href]="gallery.Website" target="_blank"
                                        class="text-dark text-decoration-none hover-link website-responsive">
                                        {{ gallery.Website }}
                                      </a>
                                    </div>
                                  </div>
                                }
                              </div>
                            </div>
                          } @else {
                            <div class="text-center mb-4">
                              <p class="text-muted">{{ 'viewArtistProfile.noContactInfo' | transloco }}</p>
                            </div>
                          }
                          <!-- Address Section -->
                          <div class="address-section mb-4">
                            <h5 class="fw-bold mb-3">{{ 'viewArtistProfile.address' | transloco }}</h5>
                            @if (gallery.GalleryAddresses && gallery.GalleryAddresses.length > 0) {
                              <div
                                class="address-display p-3 bg-light rounded">
                                <p class="text-muted mb-0">
                                  {{ gallery.GalleryAddresses[0].Address1 }},
                                  {{ gallery.GalleryAddresses[0].Address2 ?
                                  gallery.GalleryAddresses[0].Address2 + ', ' : '' }}
                                  {{ gallery.GalleryAddresses[0].SuburbTown }},
                                  {{ gallery.GalleryAddresses[0].City }},
                                  {{ gallery.GalleryAddresses[0].State }},
                                  {{ gallery.GalleryAddresses[0].PostalCode }}
                                </p>
                              </div>
                            } @else {
                              <p class="text-muted mb-0">{{ 'viewArtistProfile.noAddressAvailable' | transloco }}</p>
                            }
                          </div>
                          <!-- About Me Section -->
                          <div class="about-section mb-4">
                            <h5 class="fw-bold mb-3">{{ 'viewArtistProfile.aboutMe' | transloco }}</h5>
                            @if (gallery.AboutMe) {
                              <div class="about-content p-3 bg-light rounded"
                              [innerHTML]="gallery.AboutMe"></div>
                            } @else {
                              <p class="text-muted mb-0">{{ 'viewArtistProfile.noAboutMeInfo' | transloco }}</p>
                            }
                          </div>
                          <!-- Other Details Section -->
                          @if (gallery.GalleryType || gallery.GallerySaleType || gallery.CommissionPercentage || gallery.SalesTaxPercentage || gallery.Terms || gallery.CreatedDate) {
                            <div class="details-section mb-4"
                              >
                              <h5 class="fw-bold mb-3">{{ 'viewArtistProfile.otherDetails' | transloco }}</h5>
                              <div class="details-grid">
                                @if (gallery.GalleryType) {
                                  <div class="detail-item mb-2">
                                    <span class="fw-semibold">{{ 'viewArtistProfile.typeLabel' | transloco }}</span>
                                    <span class="badge ms-2"
                                      [ngStyle]="{'background-color': '#178d9b', 'color': 'white'}">
                                      {{ gallery.GalleryType }}
                                    </span>
                                  </div>
                                }
                                @if (gallery.GallerySaleType) {
                                  <div class="detail-item mb-2">
                                    <span class="fw-semibold">{{ 'viewArtistProfile.saleTypeLabel' | transloco }}</span>
                                    <span class="badge ms-2"
                                      [ngStyle]="{'background-color': '#178d9b', 'color': 'white'}">
                                      {{ gallery.GallerySaleType }}
                                    </span>
                                  </div>
                                }
                                @if (gallery.CommissionPercentage) {
                                  <div class="detail-item mb-2">
                                    <span class="fw-semibold">{{ 'viewArtistProfile.commissionLabel' | transloco }}</span>
                                    <span class="fw-bold ms-2">{{ gallery.CommissionPercentage
                                    }}%</span>
                                  </div>
                                }
                                @if (gallery.SalesTaxPercentage) {
                                  <div class="detail-item mb-2">
                                    <span class="fw-semibold">{{ 'viewArtistProfile.salesTaxLabel' | transloco }}</span>
                                    <span class="fw-bold ms-2">{{ gallery.SalesTaxPercentage
                                    }}%</span>
                                  </div>
                                }
                                @if (gallery.Terms) {
                                  <div class="detail-item mb-3">
                                    <span class="fw-semibold">{{ 'viewArtistProfile.termsLabel' | transloco }}</span>
                                    <div class="ms-2 text-muted" [innerHTML]="gallery.Terms"></div>
                                  </div>
                                }
                                @if (gallery.CreatedDate) {
                                  <div class="detail-item">
                                    <span class="fw-semibold">{{ 'viewArtistProfile.joinedLabel' | transloco }}</span>
                                    <span class="text-muted ms-2">{{ gallery.CreatedDate |
                                    date:'mediumDate' }}</span>
                                  </div>
                                }
                              </div>
                            </div>
                          } @else {
                            <p class="text-muted mb-0">{{ 'viewArtistProfile.noAdditionalDetails' | transloco }}</p>
                          }
                          <!-- Art Types Section -->
                          <div class="art-types-section mb-4">
                            <h5 class="fw-bold mb-3">{{ 'viewArtistProfile.artTypes' | transloco }}</h5>
                            @if (gallery.GalleryArtTypesDTOs && gallery.GalleryArtTypesDTOs.length > 0) {
                              <div class="art-types-badges d-flex flex-wrap gap-2 justify-content-center"
                                >
                                @for (artType of gallery.GalleryArtTypesDTOs; track artType) {
                                  <span
                                    class="badge"
                                    [ngStyle]="{'background-color': '#178d9b', 'color': 'white'}">
                                    {{ artType.ArtTypeDto.Name }}
                                  </span>
                                }
                              </div>
                            } @else {
                              <p class="text-muted mb-0 text-center">{{ 'viewArtistProfile.noArtTypes' | transloco }}</p>
                            }
                          </div>
                          <!-- Social Media Section -->
                          <div class="social-section">
                            <h5 class="fw-bold mb-3 text-center">{{ 'viewArtistProfile.socialMedia' | transloco }}</h5>
                            @if (gallery.FacebookLink || gallery.InstagramLink || gallery.LinkedinLink) {
                              <div class="social-links d-flex flex-wrap gap-2 justify-content-center"
                                >
                                @if (gallery.FacebookLink) {
                                  <a [href]="gallery.FacebookLink"
                                    target="_blank"
                                    class="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 social-btn">
                                    <i class="bi bi-facebook me-1"></i> {{ 'viewArtistProfile.facebook' | transloco }}
                                  </a>
                                }
                                @if (gallery.InstagramLink) {
                                  <a [href]="gallery.InstagramLink"
                                    target="_blank"
                                    class="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 social-btn">
                                    <i class="bi bi-instagram me-1"></i> {{ 'viewArtistProfile.instagram' | transloco }}
                                  </a>
                                }
                                @if (gallery.LinkedinLink) {
                                  <a [href]="gallery.LinkedinLink"
                                    target="_blank"
                                    class="btn btn-outline-primary btn-sm rounded-pill px-3 py-1 social-btn">
                                    <i class="bi bi-linkedin me-1"></i> {{ 'viewArtistProfile.linkedIn' | transloco }}
                                  </a>
                                }
                              </div>
                            } @else {
                              <p class="text-muted mb-0 text-center">{{ 'viewArtistProfile.noSocialMediaLinks' | transloco }}
                              </p>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Gallery Portfolios with improved spacing and square images -->
                  <div class="bg-white p-4 p-md-5">
                    @if (favoritePortfolioProducts.length > 0) {
                      <div>
                        <h5 class="fw-bold mb-3 fs-3 text-primary">{{ 'viewArtistProfile.featuredPortfolios' | transloco }}</h5>
                        <p class="text-muted small mb-4">{{ 'viewArtistProfile.discoverCuratedArtworks' | transloco }}</p>
                        <div class="portfolio-grid">
                          @for (portfolio of favoritePortfolioProducts; track portfolio) {
                            <div class="mb-4">
                              <div class="border-0 rounded-3">
                                @if (portfolio.FavouritePortfolioLinkViewModel?.Title) {
                                  <h4
                                    class="fw-semibold mb-2 text-dark">
                                    {{ portfolio.FavouritePortfolioLinkViewModel?.Title }}
                                  </h4>
                                }
                                @if (portfolio.FavouritePortfolioLinkViewModel?.Description) {
                                  <div
                                    class="text-muted small mb-0"
                                    [innerHTML]="portfolio.FavouritePortfolioLinkViewModel?.Description">
                                  </div>
                                }
                              </div>
                              @if (portfolio.PortfolioProductsViewModel && portfolio.PortfolioProductsViewModel.length > 0) {
                                <div class="row g-3 mt-2"
                                  >
                                  @for (rowData of portfolio.PortfolioProductsViewModel; track rowData) {
                                    <div class="col-4 col-sm-4 col-md-4 col-lg-2"
                                      >
                                      <div class="card border-0 shadow-sm rounded-3 bg-white text-center cursor-pointer h-100"
                                        (click)="showProductDeatils(rowData.ArtistProduct.Id)">
                                        <div class="card-img-top">
                                          <img [src]="rowData.ArtistProduct.ResizedImageUrl || 'assets/images/No-Image.png'"
                                            alt="{{ rowData.ArtistProduct.Name }}"
                                            class="img-fluid" />
                                          </div>
                                          <div class="card-body p-2">
                                            <h6 class="text-truncate fw-semibold small mb-0 text-dark">
                                              {{ rowData.ArtistProduct.Name
                                              }}
                                            </h6>
                                          </div>
                                        </div>
                                      </div>
                                    }
                                  </div>
                                }
                                @if (!portfolio.PortfolioProductsViewModel || portfolio.PortfolioProductsViewModel.length === 0) {
                                  <div
                                    class="text-center text-muted py-3 bg-light rounded-3">
                                    <i class="bi bi-image me-1"></i>
                                    <small>{{ 'viewArtistProfile.noArtworksAvailable' | transloco }}</small>
                                  </div>
                                }
                              </div>
                            }
                          </div>
                        </div>
                      }
                      @if (favoritePortfolioProducts.length === 0) {
                        <div
                          class="text-center text-muted py-4 bg-light rounded-3">
                          <i class="bi bi-collection me-2"></i>
                          <p class="mb-0">{{ 'viewArtistProfile.noFeaturedPortfolios' | transloco }}</p>
                        </div>
                      }
                    </div>
                  </div>
                }
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>

    <!-- Custom Terms Dialog  -->
    <p-dialog [header]="'viewArtistProfile.termsAndConditions' | transloco" [(visible)]="showTermsDialog" [modal]="true"
      [style]="{width: '45vw', maxWidth: '600px'}" [baseZIndex]="10000" [draggable]="false" [resizable]="false"
      class="terms-dialog">
      <div class="p-4">
        <!-- Modern Alert -->
        <div class="alert alert-warning alert-dismissible fade show bg-warning bg-opacity-10 border-warning border-2 rounded-3 mb-4"
          role="alert">
          <div class="d-flex align-items-center">
            <i class="pi pi-exclamation-triangle text-warning me-3" style="font-size: 1.5rem;"></i>
            <div>
              <h6 class="alert-heading mb-1 fw-bold">{{ 'viewArtistProfile.termsNotAvailable' | transloco }}</h6>
              <p class="mb-0 text-warning">{{ 'viewArtistProfile.termsTemporarilyUnavailable' | transloco }}</p>
            </div>
          </div>
        </div>

        <!-- Simple message -->
        <div class="mb-4">
          <p class="text-muted lh-lg">
            <i class="pi pi-info-circle me-2 text-info"></i>
            {{ 'viewArtistProfile.contactAdminForTerms' | transloco }}
          </p>
        </div>

        <!-- Modern Contact Card -->
        <div class="card border-0 shadow bg-light rounded-2 p-3 text-center">
          <div class="card-body">
            <div class="mb-4">
              <i class="pi pi-building " style="font-size: 3rem; color: #178d9b ;"></i>
            </div>
            <h5 class="card-title mb-3 fw-semibold" style="color: #178d9b ;">{{ 'viewArtistProfile.contactGalleryAdmin' | transloco }}</h5>
            <p class="card-text text-muted mb-4">{{ 'viewArtistProfile.sendMessageForTerms' | transloco }}</p>

            <!-- Centered Chat Button -->
            <div class="d-flex justify-content-center">
              <button pButton type="button" [label]="'viewArtistProfile.sendMessageToAdmin' | transloco" icon="pi pi-comments"
                class="p-button-outlined p-button-primary rounded-pill px-4 py-2"
                (click)="sendWhatsAppMessage()">
              </button>
            </div>

            <div class="text-muted small mt-3">
              <i class="pi pi-clock me-1"></i>
              {{ 'viewArtistProfile.wellGetBackSoon' | transloco }}
            </div>
          </div>
        </div>
      </div>
    </p-dialog>
    <!-- Product Details Dialog -->
    @if (showProductDialog) {
      <app-product-details-dialog [(showProductDialog)]="showProductDialog" [productId]="selectedProductId"
        (onClose)="closeProductDialog()">
      </app-product-details-dialog>
    }


    <!-- Original Terms Dialog -->
    @if (showOriginalTermsDialog) {
      <terms-acceptance-dialog [showDialog]="showOriginalTermsDialog" [galleryId]="galleryId"
        (showDialogChange)="onOriginalTermsDialogClose()" (onAccepted)="onTermsDialogAccepted()"
        (onCancelled)="onTermsDialogCancelled()">
      </terms-acceptance-dialog>
    }
}
<!-- end @else: standard profile layout -->
